import type { UseFormGetValues } from 'react-hook-form';
import type { ControllerRules } from '../components/fields/formController.types';
import type {
  ReportFormData,
  FormField,
  FormFieldValue,
} from '../reportCreation.types';

type FieldValidationDependencies = {
  field: FormField;
  getValues: UseFormGetValues<ReportFormData>;
  wahisAgentCodes?: string[];
};

export const toNumberValue = (value: FormFieldValue): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

export const toDateValue = (value: FormFieldValue): Date | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  return undefined;
};

const TOTAL_COUNT_SUFFIX = '.results.totalCount';

const getDateOfDetectionValidation = (): ControllerRules<ReportFormData> => ({
  validate: (value: FormFieldValue): boolean | string => {
    const date = toDateValue(value);
    if (!date) {
      return true;
    }
    if (date.getTime() > Date.now()) {
      return 'reportCreation.errors.futureDetectionDate';
    }

    return true;
  },
});

const getPopulationSizeValidation = (
  getValues: UseFormGetValues<ReportFormData>,
): ControllerRules<ReportFormData> => ({
  validate: (value: FormFieldValue): boolean | string => {
    const population = toNumberValue(value);
    if (population === undefined) {
      return true;
    }

    if (population <= 0) {
      return 'reportCreation.errors.populationMustBeGreaterThanZero';
    }

    const numberOfDeaths = toNumberValue(getValues('numberOfDeaths')) ?? 0;
    const numberOfSick = toNumberValue(getValues('numberOfSick')) ?? 0;
    const sum = numberOfDeaths + numberOfSick;

    if (sum === 0) {
      return true;
    }

    return population >= sum || 'reportCreation.errors.populationTooSmall';
  },
});

const getTestsGreaterThanPopulationValidation = (
  getValues: UseFormGetValues<ReportFormData>,
): ControllerRules<ReportFormData> => ({
  validate: (value: FormFieldValue): boolean | string => {
    const count = toNumberValue(value);
    if (count === undefined) {
      return true;
    }

    if (count < 0) {
      return 'reportCreation.errors.testsMustBeNonNegative';
    }

    const population = toNumberValue(getValues('populationSize'));
    if (population !== undefined && count > population) {
      return 'reportCreation.errors.testsGreaterThanPopulation';
    }

    return true;
  },
});

const getTotalFieldValidation = (
  fieldId: string,
  hasTotalSuffix: boolean,
  getValues: UseFormGetValues<ReportFormData>,
): ControllerRules<ReportFormData> => ({
  validate: (value: FormFieldValue): boolean | string => {
    const total = toNumberValue(value);
    if (total === undefined) {
      return true;
    }

    if (total <= 0) {
      return 'reportCreation.errors.totalTestsMustBeGreaterThanZero';
    }

    const prefix = hasTotalSuffix
      ? fieldId.slice(0, fieldId.length - TOTAL_COUNT_SUFFIX.length)
      : fieldId.replace(/results\.totalCount$/, '');

    const positivePath = prefix
      ? `${prefix}.results.positiveCount`
      : 'results.positiveCount';
    const negativePath = prefix
      ? `${prefix}.results.negativeCount`
      : 'results.negativeCount';

    const positive = toNumberValue(getValues(positivePath)) ?? 0;
    const negative = toNumberValue(getValues(negativePath)) ?? 0;
    const sum = positive + negative;

    if (total < sum) {
      return 'reportCreation.errors.totalTestsTooLow';
    }

    const population = toNumberValue(getValues('populationSize'));
    if (population !== undefined && total > population) {
      return 'reportCreation.errors.testsGreaterThanPopulation';
    }

    return true;
  },
});

const getWahisAgentSelectionValidation = (
  wahisAgentCodes: string[],
): ControllerRules<ReportFormData> | undefined => {
  if (!wahisAgentCodes.length) {
    return undefined;
  }

  return {
    validate: (value: FormFieldValue): boolean | string => {
      if (typeof value !== 'string') {
        return true;
      }

      const normalizedValue = value.trim();
      if (!normalizedValue) {
        return true;
      }

      return wahisAgentCodes.includes(normalizedValue)
        ? 'reportCreation.errors.wahisAgentInWahis'
        : true;
    },
  };
};

export const getFieldValidationRules = ({
  field,
  getValues,
  wahisAgentCodes = [],
}: FieldValidationDependencies):
  | ControllerRules<ReportFormData>
  | undefined => {
  const requiredMessage = 'reportCreation.errors.requiredField';
  const baseRules = field.required ? { required: requiredMessage } : undefined;
  const mergeRules = (
    custom?: ControllerRules<ReportFormData>,
  ): ControllerRules<ReportFormData> | undefined =>
    custom ? { ...(baseRules ?? {}), ...custom } : baseRules;

  const fieldId = String(field.id);

  if (fieldId === 'etiologyAgent') {
    return mergeRules(getWahisAgentSelectionValidation(wahisAgentCodes));
  }

  if (fieldId === 'dateOfDetection') {
    return mergeRules(getDateOfDetectionValidation());
  }

  if (fieldId === 'populationSize') {
    return mergeRules(getPopulationSizeValidation(getValues));
  }

  const isPositiveField =
    fieldId.endsWith('.results.positiveCount') ||
    fieldId === 'results.positiveCount';
  if (isPositiveField) {
    return mergeRules(getTestsGreaterThanPopulationValidation(getValues));
  }

  const isNegativeField =
    fieldId.endsWith('.results.negativeCount') ||
    fieldId === 'results.negativeCount';
  if (isNegativeField) {
    return mergeRules(getTestsGreaterThanPopulationValidation(getValues));
  }

  const hasTotalSuffix = fieldId.endsWith(TOTAL_COUNT_SUFFIX);
  const isTotalField = hasTotalSuffix || fieldId === 'results.totalCount';
  if (isTotalField) {
    return mergeRules(
      getTotalFieldValidation(fieldId, hasTotalSuffix, getValues),
    );
  }

  return mergeRules();
};
